import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { opposite, type SortDirection } from '@/api/sorting'

/** Long enough that typing a name is one request, short enough not to feel laggy. */
const SEARCH_DEBOUNCE_MS = 300

/** What somebody actually reaches for: a screenful, a working set, everything from one fair. */
export const PAGE_SIZES = [20, 50, 100, 200] as const

/** Every list endpoint answers in this shape; only the name of the array differs. */
export type PagedResponse = { page: number; size: number; total: number }

/** Which rows are on screen, counted from the page that actually came back. */
export type PageRange = { first: number; last: number; total: number }

export type PagedQuery<TSort extends string> = {
  search: string
  page: number
  size: number
  sort: TSort
  direction: SortDirection
  /** The list's own filters, as they stand in the address bar. */
  filters: Record<string, string>
}

export type PagedList<TPage extends PagedResponse, TSort extends string> = {
  /** Kept while the next request is in flight, so paging does not blank the table on the way. */
  page: TPage | null
  loading: boolean
  error: unknown
  search: string
  setSearch: (search: string) => void
  filters: Record<string, string>
  setFilter: (key: string, value: string) => void
  /** Whether anything narrows the list right now — the search term included. */
  filtered: boolean
  clearFilters: () => void
  sort: TSort
  direction: SortDirection
  /**
   * A click on a column heading. The column already sorted by turns around; a new one starts in
   * the direction that column is read in — see `directions` — so the first click lands on the
   * order somebody expected.
   */
  toggleSort: (column: TSort) => void
  pageIndex: number
  setPageIndex: (page: number) => void
  pageSize: number
  setPageSize: (size: number) => void
  pageCount: number
  /**
   * Never derived from the requested page and size. Changing the page size resets the index and
   * asks for a new page; until that lands, the rows on screen still belong to the old one, and a
   * range computed from the request would name rows that are not there.
   */
  range: PageRange
  /** After anything that changes a record — the row on screen is stale the moment it does. */
  reload: () => void
}

type Loaded<TPage> = {
  /** Which request produced this. Comparing it with the current one *is* the loading flag. */
  key: string
  page: TPage | null
  error: unknown
}

/**
 * The paging behind every list screen: debounced search, one request per settled query, the
 * previous page left on screen while the next one loads, out-of-order answers dropped, and the
 * page index clamped when the list shrinks underneath it.
 *
 * **The address bar is where all of it lives.** Search, page, page size, order and the list's own
 * filters are query parameters, not component state, which is what makes a reload land back where
 * somebody was, a link to a filtered list mean the same thing to the colleague it is sent to, and
 * the browser's back button walk back through a list rather than out of it. Everything is written
 * in one `setSearchParams` call, so a filter and the page reset that goes with it never race.
 *
 * What differs between the screens is the request and the filters, so that is all a caller passes.
 */
export function usePagedList<TPage extends PagedResponse, TSort extends string>({
  load,
  rowCount,
  filterKeys,
  directions,
  defaultSort,
  initialPageSize,
}: {
  load: (query: PagedQuery<TSort>, signal: AbortSignal) => Promise<TPage>
  /** How many rows this page actually carries — the array's name is the caller's business. */
  rowCount: (page: TPage) => number
  /** Which query parameters belong to this list, beyond the ones every list has. */
  filterKeys: readonly string[]
  /**
   * The columns this list may be ordered by, each with the direction it is read in — a date from
   * the newest, a name from A. Anything else in the URL is somebody's typo.
   *
   * One table rather than a default per list: `?sort=COMPANY_NAME` with no direction has to mean
   * A to Z, even on a list whose own default order is "newest first". It mirrors the enumeration
   * the backend applies when a request names a column and no direction.
   */
  directions: Readonly<Record<TSort, SortDirection>>
  defaultSort: TSort
  initialPageSize: number
}): PagedList<TPage, TSort> {
  const [params, setParams] = useSearchParams()
  const [reloadToken, setReloadToken] = useState(0)
  const [loaded, setLoaded] = useState<Loaded<TPage>>({ key: '', page: null, error: null })

  // The request is built from the caller's closure, which is a new function on every render.
  // Holding it in a ref lets the effect below depend on the request *key* alone: the key already
  // names every input the closure reads, so re-running on its identity would only repeat requests.
  // Refreshed in an effect of its own, declared first so that it has run by the time the request
  // effect below does — assigning during the render would be reading a ref while rendering.
  const loadRef = useRef(load)
  useEffect(() => {
    loadRef.current = load
  })

  // --- what the address bar says ---------------------------------------------------------------

  // Every one of these is read rather than validated into state: a hand-edited URL is a stranger's
  // input, and the answer to "sort=ownerId" is the default order, not a broken screen.
  const queryInUrl = params.get('q') ?? ''
  const pageIndex = pageNumberIn(params) - 1
  const pageSize = pageSizeIn(params, initialPageSize)
  const requested = params.get('sort')
  const sort = requested !== null && requested in directions ? (requested as TSort) : defaultSort
  const direction = directionIn(params) ?? directions[sort]

  const search_ = params.toString()
  const filters = useMemo(
    () => Object.fromEntries(filterKeys.map((key) => [key, params.get(key) ?? ''])),
    // `params` is a new object on every render; its string form is what actually changed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [search_, filterKeys.join(',')],
  )

  /**
   * One write for the whole change. Two `setSearchParams` calls in one tick would each start from
   * the URL as it was before the tick, and the second would quietly undo the first — which is
   * exactly what "apply the filter and go back to page one" is made of.
   */
  const update = useCallback(
    (changes: Record<string, string | null>) => {
      setParams(
        (current) => {
          const next = new URLSearchParams(current)
          for (const [key, value] of Object.entries(changes)) {
            if (value === null || value === '') {
              next.delete(key)
            } else {
              next.set(key, value)
            }
          }
          return next
        },
        // Replaced rather than pushed: a filter is not a place somebody navigated to, and pushing
        // would make the back button walk through every keystroke instead of leaving the list.
        { replace: true },
      )
    },
    [setParams],
  )

  // --- the search term, which is typed rather than picked ---------------------------------------

  // Held locally as well, because the input has to answer every keystroke while the URL — and the
  // request with it — only moves once the typing settles.
  const [search, setSearch] = useState(queryInUrl)
  const [lastQuery, setLastQuery] = useState(queryInUrl)
  if (lastQuery !== queryInUrl) {
    // The address bar moved on its own: the back button, or a link somebody opened. The field
    // follows it, and the debounce below then has nothing left to write.
    setLastQuery(queryInUrl)
    setSearch(queryInUrl)
  }

  useEffect(() => {
    if (search === queryInUrl) {
      return
    }
    const timer = setTimeout(
      // A narrower search almost never has the page the user was on; starting anywhere but the
      // first one shows an empty table for results that do exist.
      () => update({ q: search, page: null }),
      SEARCH_DEBOUNCE_MS,
    )
    return () => clearTimeout(timer)
  }, [search, queryInUrl, update])

  // --- the request ------------------------------------------------------------------------------

  const requestKey = JSON.stringify([
    queryInUrl,
    filters,
    sort,
    direction,
    pageIndex,
    pageSize,
    reloadToken,
  ])
  // Derived rather than a `setLoading(true)` at the top of the effect, which would be a second
  // render before the request has even gone out.
  const loading = loaded.key !== requestKey

  useEffect(() => {
    // Typing fires a request per debounce window; without this an early, slower one can land last
    // and paint results for a query the user has already moved on from.
    const controller = new AbortController()

    loadRef
      .current(
        {
          search: queryInUrl,
          page: pageIndex,
          size: pageSize,
          sort,
          direction,
          filters,
        },
        controller.signal,
      )
      .then((page) => {
        if (controller.signal.aborted) {
          return
        }
        // Deleting the last row of the last page shrinks the list under the page the user is on.
        // Without this they are left looking at an empty table that claims to hold records, with
        // the pagination gone too — `pageCount` has dropped to 1 by then.
        const lastPage = Math.max(0, Math.ceil(page.total / page.size) - 1)
        if (pageIndex > lastPage) {
          update({ page: lastPage === 0 ? null : String(lastPage + 1) })
          return
        }
        setLoaded({ key: requestKey, page, error: null })
      })
      .catch((error: unknown) => {
        if (!controller.signal.aborted) {
          setLoaded({ key: requestKey, page: null, error })
        }
      })

    return () => controller.abort()
  }, [requestKey, queryInUrl, filters, sort, direction, pageIndex, pageSize, update])

  // --- what the screen does to it ---------------------------------------------------------------

  const setFilter = useCallback(
    (key: string, value: string) => update({ [key]: value, page: null }),
    [update],
  )

  const clearFilters = useCallback(
    () => update({ q: null, page: null, ...Object.fromEntries(filterKeys.map((key) => [key, null])) }),
    [update, filterKeys],
  )

  const toggleSort = useCallback(
    (column: TSort) =>
      update({
        sort: column,
        dir: column === sort ? opposite(direction) : directions[column],
        // A different order is a different first page; the row somebody was looking at is not on
        // page seven of it.
        page: null,
      }),
    [update, sort, direction, directions],
  )

  const setPageIndex = useCallback(
    (next: number) => update({ page: next <= 0 ? null : String(next + 1) }),
    [update],
  )

  // Back to the first page rather than trying to keep the rows in view: with a larger page the
  // old index points somewhere else entirely, and "page 7 of 3" is how a list ends up empty.
  const setPageSize = useCallback(
    (next: number) => update({ size: next === initialPageSize ? null : String(next), page: null }),
    [update, initialPageSize],
  )

  const reload = useCallback(() => setReloadToken((token) => token + 1), [])

  const page = loaded.page

  return {
    page,
    loading,
    error: loaded.error,
    search,
    setSearch,
    filters,
    setFilter,
    filtered: queryInUrl !== '' || filterKeys.some((key) => (params.get(key) ?? '') !== ''),
    clearFilters,
    sort,
    direction,
    toggleSort,
    pageIndex,
    setPageIndex,
    pageSize,
    setPageSize,
    pageCount: Math.max(1, Math.ceil((page?.total ?? 0) / (page?.size ?? pageSize))),
    range: {
      first: page && page.total > 0 ? page.page * page.size + 1 : 0,
      last: page ? page.page * page.size + rowCount(page) : 0,
      total: page?.total ?? 0,
    },
    reload,
  }
}

/** One-based in the address bar, because that is the number printed next to it on screen. */
function pageNumberIn(params: URLSearchParams): number {
  const parsed = Number(params.get('page'))
  return Number.isInteger(parsed) && parsed >= 1 ? parsed : 1
}

/** Only a size the selector offers: a hand-typed one would show a number nobody can change back. */
function pageSizeIn(params: URLSearchParams, fallback: number): number {
  const parsed = Number(params.get('size'))
  return PAGE_SIZES.find((size) => size === parsed) ?? fallback
}

function directionIn(params: URLSearchParams): SortDirection | null {
  const raw = params.get('dir')
  return raw === 'ASC' || raw === 'DESC' ? raw : null
}
